/**
 * Tentpole Calendar — shared formatting + status-pill mapping.
 * Date helpers re-exported from Takeovers/shared: one implementation of the
 * date-only `T00:00:00` parse guard.
 */

import { StatusVariant } from '../../ui';
import {
  CalendarDatePrecision,
  CalendarEntry,
  CalendarEntryStatus,
  CalendarPlanStatus,
  CalendarRecurrence,
  CONTENT_TYPE_LABELS,
  PLAN_STATUS_LABELS,
  RECURRENCE_LABELS,
  Tentpole,
} from './types';

import { fmtDate } from '../../Takeovers/shared';

export { fmtDate, fmtDateTime } from '../../Takeovers/shared';

/** Shared copy for the two deliberately separate confidentiality fields. */
export const CALENDAR_FIELD_LABELS = {
  advertiserSummary: 'What advertisers can know',
  creatorPrivateNote: 'Private note from creator · CG only',
  internalNote: 'Internal CG note',
} as const;

/** V2 name first, with the v1 alias kept readable during migration. */
export const advertiserSummaryOf = (
  entry: Pick<CalendarEntry, 'advertiserSummary' | 'externalDescription'>,
): string => entry.advertiserSummary ?? entry.externalDescription ?? '';

/**
 * Legacy entries predate these planning fields. Never infer confidence or a
 * repeat cadence from an old row; surface the explicit Unknown option instead.
 */
export const DEFAULT_CALENDAR_RECURRENCE: CalendarRecurrence = 'unknown';
export const DEFAULT_CALENDAR_PLAN_STATUS: CalendarPlanStatus = 'unknown';

export const recurrenceOf = (
  entry?: Pick<CalendarEntry, 'recurrence'> | null,
): CalendarRecurrence => entry?.recurrence ?? DEFAULT_CALENDAR_RECURRENCE;

export const planStatusOf = (
  entry?: Pick<CalendarEntry, 'planStatus'> | null,
): CalendarPlanStatus => entry?.planStatus ?? DEFAULT_CALENDAR_PLAN_STATUS;

/** Snapshot labels in stored order, falling back to legacy single-channel data. */
export function targetChannelLabels(
  entry: Pick<CalendarEntry, 'targetChannelIds' | 'targetChannels' | 'channelId' | 'channelLabel'>,
): string[] {
  if (entry.targetChannels && entry.targetChannels.length > 0) {
    return entry.targetChannels.map((channel) => channel.channelLabel || channel.channelId);
  }
  if (entry.targetChannelIds && entry.targetChannelIds.length > 0) return entry.targetChannelIds;
  if (entry.channelId || entry.channelLabel) return [entry.channelLabel || entry.channelId!];
  return [];
}

/** One consistent channel-scope label for rows, queue cells, chips, and detail. */
export function targetScopeLabel(
  entry: Pick<CalendarEntry, 'scopeMode' | 'targetChannelIds' | 'targetChannels' | 'channelId' | 'channelLabel'>,
  compact = false,
): string {
  const labels = targetChannelLabels(entry);
  if (entry.scopeMode === 'all_page_snapshot') {
    if (compact) return labels.length > 0 ? `All ${labels.length} channels` : 'All channels';
    return labels.length > 0 ? `All channels · ${labels.length}-channel snapshot` : 'All channels';
  }
  if (labels.length === 1) return labels[0];
  if (labels.length > 1) return compact ? `${labels.length} channels` : labels.join(', ');
  return 'All channels';
}

/**
 * Channel-first identity for admin surfaces. The ad team sells against the
 * channel, so the targeted channel leads and the authoring page becomes a
 * byline when its name adds information — "Hyphen8 Kids · by All Def Media".
 * Page-wide snapshots keep the page as the identity (no single channel to
 * feature) and get the scope summary as `secondary` instead.
 *
 * The byline is suppressed only when the page label is VISIBLE in the primary
 * (the lead label, or either label in the two-channel form) — a match hidden
 * behind "+N" still needs attribution. Exactly one of byline/secondary is set,
 * so rows never have to compare display strings to decide what to render.
 */
export function entryDisplayParts(
  entry: Pick<
    CalendarEntry,
    'scopeMode' | 'targetChannelIds' | 'targetChannels' | 'channelId' | 'channelLabel' | 'creatorLabel'
  >,
): { primary: string; byline?: string; secondary?: string } {
  const labels = targetChannelLabels(entry);
  if (entry.scopeMode === 'all_page_snapshot' || labels.length === 0) {
    return { primary: entry.creatorLabel, secondary: targetScopeLabel(entry, true) };
  }
  const primary =
    labels.length === 1
      ? labels[0]
      : labels.length === 2
        ? `${labels[0]} + ${labels[1]}`
        : `${labels[0]} +${labels.length - 1}`;
  const visible = labels.length === 2 ? labels.slice(0, 2) : labels.slice(0, 1);
  return visible.includes(entry.creatorLabel) ? { primary } : { primary, byline: entry.creatorLabel };
}

export const ENTRY_STATUS_VARIANT: Record<CalendarEntryStatus, StatusVariant> = {
  submitted: 'pending', // amber — action needed
  approved: 'ok', // green — on the calendar
  declined: 'paused', // gray
  withdrawn: 'draft', // neutral
};

/** "1 plan" / "8 plans" — the calendar's one word for an entry. */
export const plansWord = (n: number): string => `${n} ${n === 1 ? 'plan' : 'plans'}`;

/** "Oct 1 – Oct 31, 2026" window label. */
export const fmtTentpoleWindow = (t: Pick<Tentpole, 'startDate' | 'endDate'>): string =>
  `${fmtDate(t.startDate)} → ${fmtDate(t.endDate)}`;

/** Planned-date phrasing at the entry's precision. */
export function fmtPlannedDate(entry: { plannedDate: string; datePrecision: CalendarDatePrecision }): string {
  if (entry.datePrecision === 'month') {
    const d = new Date(`${entry.plannedDate}T00:00:00`);
    return Number.isNaN(d.getTime())
      ? '—'
      : d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }
  const day = fmtDate(entry.plannedDate);
  return entry.datePrecision === 'week' ? `Week of ${day}` : day;
}

// 'en-CA' yields YYYY-MM-DD. Hoisted: todayYmd runs once per row on every filter pass.
const LA_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The business's today: the America/Los_Angeles date, whatever the browser's
 *  own zone (a Tokyo morning is still the previous day in LA). */
export const todayYmd = (): string => LA_DAY_FMT.format(new Date());

/** The LA calendar date of an instant such as createdAt ('' if unparseable). */
export const laYmdOf = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : LA_DAY_FMT.format(d);
};

/** "Today" / "Tomorrow" / "In 3 days" through the coming week (LA dates),
 *  otherwise null: the agenda's at-a-glance urgency cue. */
export function relativeDayLabel(ymd: string): string | null {
  const days = ymdDiffDays(todayYmd(), ymd);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  return days > 1 && days < 7 ? `In ${days} days` : null;
}

/** "today" / "yesterday" / "19 days ago" for an instant, by LA dates. */
export function fmtDaysAgo(iso: string): string {
  const day = laYmdOf(iso);
  if (!day) return '';
  const days = ymdDiffDays(day, todayYmd());
  if (days <= 0) return 'today';
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

/** '🎃 Halloween 2026' — the one emoji-prefixed display label for a tentpole,
 *  matching the creator calendar's item label (calendarModel.ts). */
export const tentpoleLabel = (t: Pick<Tentpole, 'emoji' | 'name'>): string =>
  `${t.emoji ? `${t.emoji} ` : ''}${t.name}`;

/** "In 24 days" / "Happening now" / "Ended". */
export function tentpoleTiming(t: Pick<Tentpole, 'startDate' | 'endDate'>): {
  label: string;
  phase: 'upcoming' | 'live' | 'past';
} {
  const today = todayYmd();
  if (today < t.startDate) {
    const days = Math.round(
      (new Date(`${t.startDate}T00:00:00`).getTime() - new Date(`${today}T00:00:00`).getTime()) / 86_400_000,
    );
    return { label: days === 1 ? 'Tomorrow' : `In ${days} days`, phase: 'upcoming' };
  }
  if (today <= t.endDate) return { label: 'Happening now', phase: 'live' };
  return { label: 'Ended', phase: 'past' };
}

export const isUpcomingTentpole = (t: Pick<Tentpole, 'endDate'>): boolean => t.endDate >= todayYmd();

// YMD arithmetic in UTC, as backend/src/utils/ymd.ts does it, so the browser's
// zone and DST can never shift a day.
const addDaysYmd = (ymd: string, days: number): string =>
  new Date(Date.parse(`${ymd}T00:00:00Z`) + days * 86_400_000).toISOString().slice(0, 10);

/** Whole days from `from` to `to` (both YMD), in the same UTC arithmetic. */
export const ymdDiffDays = (from: string, to: string): number =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/** Last calendar day of the month holding `ymd`: '2028-02-10' → '2028-02-29'. */
const monthEndOfYmd = (ymd: string): string =>
  new Date(Date.UTC(parseInt(ymd.slice(0, 4), 10), parseInt(ymd.slice(5, 7), 10), 0)).toISOString().slice(0, 10);

/**
 * The last day a plan covers — mirrors calendarEntryEffectiveEndYmd in
 * backend/src/services/tentpoleCalendarRules.ts ('' reads as no end date).
 * Month plans store the 1st, so they run to the end of their (last) month; a
 * week plan with no end date runs through the six days after its start; an
 * exact plan ends on its end date or its start.
 */
export function calendarEntryEffectiveEndYmd(e: {
  plannedDate: string;
  plannedEndDate?: string;
  datePrecision?: CalendarDatePrecision;
}): string {
  if (e.datePrecision === 'month') return monthEndOfYmd(e.plannedEndDate || e.plannedDate);
  if (e.plannedEndDate) return e.plannedEndDate;
  return e.datePrecision === 'week' ? addDaysYmd(e.plannedDate, 6) : e.plannedDate;
}

/**
 * Timeframe test for ENTRIES (agenda + custom sections): a plan stays upcoming
 * through its effective end — a range still in flight, a month plan all month,
 * "week of Oct 12" through Oct 18.
 */
export const isUpcomingEntry = (e: {
  plannedDate: string;
  plannedEndDate?: string;
  datePrecision?: CalendarDatePrecision;
}): boolean => calendarEntryEffectiveEndYmd(e) >= todayYmd();

/**
 * fmtPlannedDate plus the range affix: "Sep 14, 2026 → Jan 9, 2027" (the
 * fmtTentpoleWindow arrow idiom); a range inside one year names the year
 * once — "Oct 20 → Oct 27, 2026", "September → December 2026". Use at every
 * render site; grouping keys stay on plannedDate.
 */
export function fmtPlannedRange(entry: {
  plannedDate: string;
  plannedEndDate?: string;
  datePrecision: CalendarDatePrecision;
}): string {
  const start = fmtPlannedDate(entry);
  if (!entry.plannedEndDate) return start;
  // Month precision spans whole months (both ends stored as the 1st) —
  // "September 2026 → February 2027", never "→ Feb 1, 2027".
  const end =
    entry.datePrecision === 'month'
      ? fmtPlannedDate({ plannedDate: entry.plannedEndDate, datePrecision: 'month' })
      : fmtDate(entry.plannedEndDate);
  const year = entry.plannedDate.slice(0, 4);
  const sameYear = entry.plannedEndDate.slice(0, 4) === year;
  return `${sameYear ? start.replace(new RegExp(`,? ${year}$`), '') : start} → ${end}`;
}

const shortMonthDay = (ymd: string): string =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const monthName = (ymd: string, month: 'short' | 'long'): string =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { month });

/**
 * The end of a ranged plan, short: "Oct 27"; "Jan 9, 2027" when it ends in a
 * later year than it starts; "Feb 2027" for month plans. '' without an end.
 */
export function fmtRangeEnd(entry: {
  plannedDate: string;
  plannedEndDate?: string;
  datePrecision: CalendarDatePrecision;
}): string {
  const end = entry.plannedEndDate;
  if (!end) return '';
  if (entry.datePrecision === 'month') {
    return new Date(`${end}T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  return end.slice(0, 4) === entry.plannedDate.slice(0, 4) ? shortMonthDay(end) : fmtDate(end);
}

/**
 * A row's compact date column, without the year: "Oct 18", "Oct 20 → 27",
 * "Oct 28 → Nov 3", "Week of Oct 12", "October", "Sep → Feb". The row adds
 * the year underneath when the plan starts outside today's year, and keeps
 * the full fmtPlannedRange in its tooltip.
 */
export function fmtShortPlanned(entry: {
  plannedDate: string;
  plannedEndDate?: string;
  datePrecision: CalendarDatePrecision;
}): string {
  const { plannedDate: start, plannedEndDate: end, datePrecision } = entry;
  if (datePrecision === 'month') {
    return end ? `${monthName(start, 'short')} → ${monthName(end, 'short')}` : monthName(start, 'long');
  }
  const label = datePrecision === 'week' ? `Week of ${shortMonthDay(start)}` : shortMonthDay(start);
  if (!end) return label;
  const endLabel = end.slice(0, 7) === start.slice(0, 7) ? String(Number(end.slice(8, 10))) : shortMonthDay(end);
  return `${label} → ${endLabel}`;
}

/**
 * The quiet second line under a row's "Channel  Title" headline: short facts
 * in display order, which the row joins with " · ".
 *
 * `showsDates`: the row already has its own date column (tentpole sections),
 * so a range's end would only repeat it.
 */
export function planMetaParts(
  entry: CalendarEntry,
  { showsDates = false }: { showsDates?: boolean } = {},
): string[] {
  const { byline, secondary } = entryDisplayParts(entry);
  const recurrence = recurrenceOf(entry);
  const planStatus = planStatusOf(entry);

  // Every fact a row could show, already worded (undefined = nothing to say).
  const who = byline ? `by ${byline}` : secondary; // "by Northside Network" · "All 3 channels"
  const format = CONTENT_TYPE_LABELS[entry.contentType]; // "Livestream"
  const cadence = RECURRENCE_LABELS[recurrence]; // "Daily" · "One-Time" · "Unknown"
  const plan = `Plan: ${PLAN_STATUS_LABELS[planStatus]}`; // "Plan: Likely" · "Plan: Unknown"
  const through = entry.plannedEndDate ? `through ${fmtRangeEnd(entry)}` : undefined; // "through Oct 27"
  const addedBy = entry.source === 'admin'
    ? `Added by CG${entry.createdVia === 'mcp' ? ' via Claude' : ''}`
    : undefined;

  // Drop the facts that say nothing: One-Time is the default cadence, Unknown
  // marks a legacy row, and a date column already shows the range's end.
  // Provenance goes last.
  return [
    who,
    format,
    recurrence === 'one_time' || recurrence === 'unknown' ? undefined : cadence,
    planStatus === 'unknown' ? undefined : plan,
    showsDates ? undefined : through,
    addedBy,
  ].filter((fact): fact is string => !!fact);
}

/**
 * Month-grid chip palette — the same tokens StatusPill's pending/ok/paused/
 * draft variants use, so the two surfaces can never disagree.
 */
export const ENTRY_STATUS_CHIP_COLORS: Record<CalendarEntryStatus, { bg: string; fg: string }> = {
  submitted: { bg: 'var(--warning-soft)', fg: 'var(--warning)' },
  approved: { bg: 'var(--success-soft)', fg: 'var(--success)' },
  declined: { bg: 'var(--cg-neutral-100)', fg: 'var(--fg-3)' },
  withdrawn: { bg: 'var(--cg-neutral-100)', fg: 'var(--fg-3)' },
};

// ---------------------------------------------------------------------------
// Month-grid date math (pure string / Date(y, m, d) arithmetic — no libs)
// ---------------------------------------------------------------------------

/** 'YYYY-MM' of a YMD string. */
export const monthKeyOf = (ymd: string): string => ymd.slice(0, 7);

export function addMonths(monthKey: string, delta: number): string {
  const y = parseInt(monthKey.slice(0, 4), 10);
  const m = parseInt(monthKey.slice(5, 7), 10) - 1;
  const d = new Date(y, m + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "September 2026" heading for a 'YYYY-MM' key. */
export const monthLabel = (monthKey: string): string =>
  new Date(`${monthKey}-01T00:00:00`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

/** 'Mon' for a YMD string. */
export const weekdayShort = (ymd: string): string =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' });

/** 'Monday' for a YMD string. */
export const weekdayLong = (ymd: string): string =>
  new Date(`${ymd}T00:00:00`).toLocaleDateString('en-US', { weekday: 'long' });

/** "Q1 2027" for a YMD string — the tentpole directory's grouping unit. */
export const quarterLabel = (ymd: string): string => {
  const y = ymd.slice(0, 4);
  const m = parseInt(ymd.slice(5, 7), 10);
  return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
};
